/**
 * Categories Service — connectour-admin
 *
 * Toda operação de create / update / delete é espelhada simultaneamente
 * no banco (default) e no banco Enterprise do AI Studio via Promise.all.
 * Garantia: se um falhar, o erro é propagado e o chamador é notificado.
 */

import {
  doc,
  setDoc,
  collection,
  deleteDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db, dbAiStudio } from "../firebase";
import { TABLES } from "../enums/tables";
import { trimObjectValues } from "../utils/trim";

// ── Helpers internos ─────────────────────────────────────────────────────────

/**
 * Monta o payload limpo de uma categoria.
 * Centralizado para garantir consistência entre os dois bancos.
 */
function buildCategoryPayload({ color, names, collection: type, subs, extra = {} }) {
  const cleanSubs = Array.isArray(subs)
    ? subs.map((sub) => ({
        id: sub.id || "",
        names: trimObjectValues(sub.names || {}),
      }))
    : [];

  return {
    color,
    names: trimObjectValues(names),
    collection: type,
    subs: cleanSubs,
    ...extra,
  };
}

/**
 * Escreve/atualiza um documento de categoria nos dois bancos simultaneamente.
 */
async function syncSet(docId, payload) {
  const refDefault   = doc(db,          TABLES.CATEGORIES, docId);
  const refAiStudio  = doc(dbAiStudio,  TABLES.CATEGORIES, docId);
  await Promise.all([
    setDoc(refDefault,  payload, { merge: true }),
    setDoc(refAiStudio, payload, { merge: true }),
  ]);
}

/**
 * Atualiza parcialmente um documento nos dois bancos.
 */
async function syncUpdate(docId, payload) {
  const refDefault   = doc(db,          TABLES.CATEGORIES, docId);
  const refAiStudio  = doc(dbAiStudio,  TABLES.CATEGORIES, docId);
  await Promise.all([
    updateDoc(refDefault,  payload),
    updateDoc(refAiStudio, payload),
  ]);
}

/**
 * Deleta um documento dos dois bancos.
 */
async function syncDelete(docId) {
  const refDefault   = doc(db,          TABLES.CATEGORIES, docId);
  const refAiStudio  = doc(dbAiStudio,  TABLES.CATEGORIES, docId);
  await Promise.all([
    deleteDoc(refDefault),
    deleteDoc(refAiStudio),
  ]);
}

// ── API pública ──────────────────────────────────────────────────────────────

/**
 * Cria uma nova categoria nos dois bancos.
 * O ID é gerado automaticamente pelo Firestore e compartilhado entre os dois.
 */
export async function createCategory(categoriesData) {
  const { color, names, collection: type, subs } = categoriesData;

  // Gera o ref no banco principal para obter o ID
  const documentRef = doc(collection(db, TABLES.CATEGORIES));
  const now = serverTimestamp();

  const payload = buildCategoryPayload({
    color, names, collection: type, subs,
    extra: {
      id:         documentRef.id,
      active:     true,
      created_at: now,
      updated_at: now,
    },
  });

  // Escreve nos dois bancos com o mesmo ID
  await syncSet(documentRef.id, payload);

  return documentRef.id;
}

/**
 * Atualiza uma categoria existente nos dois bancos.
 */
export async function updateCategory(categoriesData) {
  const { color, names, collection: type, id, subs } = categoriesData;

  const payload = buildCategoryPayload({
    color, names, collection: type, subs,
    extra: { updated_at: serverTimestamp() },
  });

  await syncUpdate(id, payload);
}

/**
 * Remove uma categoria dos dois bancos.
 */
export async function remove(categoriesData) {
  const { id } = categoriesData;
  await syncDelete(id);
}
